import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Seq, Set } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TcsCustomPermission } from 'src/app/shared/authorization/permissions';
import { CompanyContextService } from 'src/app/shared/company-context.service';
import { EntityType, UserRole } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import {
  CommunicationCenterRecord,
  selectAllCommunicationCenterEntities,
} from 'src/store/generated/communication-center-record.generated';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { PermissionsService } from '../amg-authorization/permissions.service';
import { UserService } from '../tcs-identity/user.service';
import { AccessibleItemAuthorizationService } from './accessible-item-authorization.service';

@Injectable({ providedIn: 'root' })
export class OpsReaderAuthorizationService {
  constructor(
    private _user: UserService,
    private _accessibleItems: AccessibleItemAuthorizationService,
    private _permissions: PermissionsService,
    private _store: Store<AppState>,
    private _companyContextService: CompanyContextService,
  ) {}

  public isReadonlyForCommCenter(commCenterId: string): Observable<boolean> {
    return this.getReadonlyCommCenters().pipe(
      map((_) => _.find((cc) => cc.id === commCenterId) != null),
    );
  }

  public isReadonlyForCompany(companyId: string): Observable<boolean> {
    return this.getReadonlyCommCenters(companyId).pipe(
      map((ccs) => ccs.find((cc) => cc.companyId === companyId) != null),
    );
  }

  public isReadonlyForCommCenterAndCurrentCompany(commCenterId: string): Observable<boolean> {
    return this.getReadonlyCommCentersForCurrentCompany().pipe(
      map((_) => _.find((cc) => cc.id === commCenterId) != null),
    );
  }

  public getReadonlyCommCentersForCurrentCompany(): Observable<
    Seq.Indexed<CommunicationCenterRecord>
  > {
    return this._companyContextService.company$.pipe(
      filterNull(),
      switchMap((company) => this.getReadonlyCommCenters(company.id)),
    );
  }

  public getReadonlyCommCenters(
    companyId?: string,
  ): Observable<Seq.Indexed<CommunicationCenterRecord>> {
    return this._user.user$.pipe(
      switchMap((user) => {
        if (user == null) {
          return of(Seq.Indexed<CommunicationCenterRecord>());
        }
        const accessibleItems$ = this._accessibleItems.getAccessibleItems();
        const commCenters$ = this._store
          .select(selectAllCommunicationCenterEntities)
          .pipe(
            map((companies) =>
              companies.filter((c) => companyId == null || companyId === c.companyId).valueSeq(),
            ),
          );
        return combineLatest([accessibleItems$, commCenters$]).pipe(
          map(([accessibleItems, commCenters]) => {
            if (this._hasScopedPermission(user)) {
              // User has global permission to access all comm centers
              return commCenters;
            }

            return commCenters.filter((cc) => {
              const companyAi = accessibleItems.find(
                (ai) => ai.entityId === cc.companyId && ai.type === EntityType.Company,
              );
              if (this._hasScopedPermission(companyAi)) {
                // User has been granted access to this comm center because
                // they have accesss to all comm centers in this company
                return true;
              }
              const commCenterAi = accessibleItems.find(
                (ai) => ai.entityId === cc.id && ai.type === EntityType.CommunicationCenter,
              );
              if (this._hasScopedPermission(commCenterAi)) {
                // User has been granted access to this specific comm center
                return true;
              }
              return false;
            });
          }),
        );
      }),
    );
  }

  private _hasScopedPermission(
    scope:
      | {
          userRoles: Set<UserRole> | null;
        }
      | undefined,
  ) {
    if (scope == null) {
      return false;
    }
    const { userRoles } = scope;
    if (userRoles == null) {
      return false;
    }
    return this._permissions.hasPermission(
      userRoles.toArray().filter((r) => r !== UserRole.Developer),
      TcsCustomPermission.ReadOnlyCommCentersWithinScope,
    );
  }
}
